export const LECTURE_REVISION_INTERVALS = [1, 3, 7, 15, 30, 60, 75] as const;
export const MAX_DAILY_REVISIONS = 8;
export const MAX_LECTURES = 20;

export type LectureRevision = {
  id: string;
  lectureId: string;
  lectureNumber: number;
  revisionNumber: number;
  intervalDays: number;
  dueDate: string;
  status: "pending" | "completed";
  completedAt: string | null;
};

export type Lecture = {
  id: string;
  number: number;
  completedOn: string | null;
};

export type StudyChapter = {
  id: string;
  subject: string;
  chapter: string;
  totalLectures: number;
  createdAt: string;
  lectures: Lecture[];
  revisions: LectureRevision[];
};

export type ChapterStats = {
  completed: number;
  remaining: number;
  percent: number;
  dueToday: number;
  overdue: number;
  nextRevision: string | null;
  lastRevision: string | null;
  completionDate: string | null;
  status: "not-started" | "in-progress" | "revision-due" | "on-track" | "completed";
};

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function chapterStats(chapter: StudyChapter, today: string): ChapterStats {
  const done = chapter.lectures.filter((l) => l.completedOn);
  const completed = done.length;
  const remaining = Math.max(0, chapter.totalLectures - completed);
  const percent = chapter.totalLectures
    ? Math.round((completed / chapter.totalLectures) * 100)
    : 0;

  const pending = chapter.revisions.filter((r) => r.status === "pending");
  const dueToday = pending.filter((r) => r.dueDate === today).length;
  const overdue = pending.filter((r) => r.dueDate < today).length;

  const upcoming = pending
    .filter((r) => r.dueDate > today)
    .map((r) => r.dueDate)
    .sort();
  const lastDone = chapter.revisions
    .filter((r) => r.status === "completed")
    .map((r) => r.dueDate)
    .sort();
  const completionDates = done.map((l) => l.completedOn as string).sort();

  const status: ChapterStats["status"] =
    completed === 0
      ? "not-started"
      : dueToday + overdue > 0
        ? "revision-due"
        : remaining > 0
          ? "in-progress"
          : pending.length === 0
            ? "completed"
            : "on-track";

  return {
    completed,
    remaining,
    percent,
    dueToday,
    overdue,
    nextRevision: upcoming[0] ?? null,
    lastRevision: lastDone.length ? lastDone[lastDone.length - 1] : null,
    completionDate: completionDates.length ? completionDates[completionDates.length - 1] : null,
    status,
  };
}

export type PlanGroup = {
  chapterId: string;
  subject: string;
  chapter: string;
  revisions: LectureRevision[];
};

/**
 * Builds today's balanced revision plan: every pending revision due today or
 * earlier is a candidate (missed ones carry forward automatically), then we
 * round-robin across subjects so a single subject never floods the day.
 */
export function buildDailyPlan(
  chapters: StudyChapter[],
  today: string,
  limit = MAX_DAILY_REVISIONS,
): PlanGroup[] {
  type Item = { chapter: StudyChapter; revision: LectureRevision };
  const bySubject = new Map<string, Item[]>();

  for (const chapter of chapters) {
    for (const revision of chapter.revisions) {
      if (revision.status !== "pending" || revision.dueDate > today) continue;
      const list = bySubject.get(chapter.subject) ?? [];
      list.push({ chapter, revision });
      bySubject.set(chapter.subject, list);
    }
  }

  for (const list of bySubject.values()) {
    list.sort(
      (a, b) =>
        a.revision.dueDate.localeCompare(b.revision.dueDate) ||
        a.revision.revisionNumber - b.revision.revisionNumber ||
        a.revision.lectureNumber - b.revision.lectureNumber,
    );
  }

  const queues = Array.from(bySubject.entries()).sort((a, b) => b[1].length - a[1].length);
  const picked: Item[] = [];
  let index = 0;
  while (picked.length < limit && queues.some(([, list]) => list.length > 0)) {
    const queue = queues[index % queues.length][1];
    const next = queue.shift();
    if (next) picked.push(next);
    index += 1;
  }

  const groups = new Map<string, PlanGroup>();
  for (const item of picked) {
    const group = groups.get(item.chapter.id) ?? {
      chapterId: item.chapter.id,
      subject: item.chapter.subject,
      chapter: item.chapter.chapter,
      revisions: [],
    };
    group.revisions.push(item.revision);
    groups.set(item.chapter.id, group);
  }

  return Array.from(groups.values()).map((g) => ({
    ...g,
    revisions: g.revisions.sort((a, b) => a.lectureNumber - b.lectureNumber),
  }));
}

export type CalendarEntry = {
  date: string;
  completions: { subject: string; chapter: string; lectures: number }[];
  revisions: { subject: string; chapter: string; lectures: number; missed: boolean }[];
};

export function buildCalendar(chapters: StudyChapter[], today: string): Map<string, CalendarEntry> {
  const map = new Map<string, CalendarEntry>();
  const entry = (date: string) => {
    const found = map.get(date) ?? { date, completions: [], revisions: [] };
    map.set(date, found);
    return found;
  };

  for (const chapter of chapters) {
    const byDate = new Map<string, number>();
    for (const lecture of chapter.lectures) {
      if (!lecture.completedOn) continue;
      byDate.set(lecture.completedOn, (byDate.get(lecture.completedOn) ?? 0) + 1);
    }
    for (const [date, lectures] of byDate) {
      entry(date).completions.push({ subject: chapter.subject, chapter: chapter.chapter, lectures });
    }

    const revByDate = new Map<string, { count: number; missed: boolean }>();
    for (const revision of chapter.revisions) {
      if (revision.status !== "pending") continue;
      const found = revByDate.get(revision.dueDate) ?? { count: 0, missed: false };
      found.count += 1;
      found.missed = found.missed || revision.dueDate < today;
      revByDate.set(revision.dueDate, found);
    }
    for (const [date, info] of revByDate) {
      entry(date).revisions.push({
        subject: chapter.subject,
        chapter: chapter.chapter,
        lectures: info.count,
        missed: info.missed,
      });
    }
  }

  return map;
}

export function statusMeta(status: ChapterStats["status"]) {
  switch (status) {
    case "revision-due":
      return { label: "Revision due", classes: "border-warning/30 bg-warning/10 text-warning" };
    case "completed":
      return { label: "Mastered", classes: "border-success/30 bg-success/10 text-success" };
    case "on-track":
      return { label: "On track", classes: "border-primary/30 bg-primary/10 text-primary" };
    case "in-progress":
      return { label: "In progress", classes: "border-hairline bg-surface text-muted-foreground" };
    default:
      return { label: "Not started", classes: "border-hairline bg-surface text-muted-foreground" };
  }
}